import { ChevronLeft } from 'lucide-react';
import React, { useEffect } from 'react';
import styled, { ThemeProvider, createGlobalStyle, css } from 'styled-components';

/* ======================================================
   THEME (SME Cap Advisory – light)
====================================================== */

const theme = {
  bg: '#e8eaed',
  bgAlt: '#ffffff',
  card: 'rgba(255,255,255,0.9)',
  text: '#111827',
  subText: '#4b5563',
  accent: '#000000',
  border: 'rgba(0,0,0,0.08)',
};

const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    background: ${theme.bg};
    color: ${theme.text};
    font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial;
  }
`;

/* ======================================================
   PAGE
====================================================== */

const PrivacyPage = () => {
  useEffect(() => window.scrollTo(0, 0), []);

  const path = window.location.pathname;

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />

      <Page>
        {/* --- NAV --- */}
        <Nav>
          <NavInner>
            <NavLeft onClick={() => (window.location.href = '/')}>
              <ChevronLeft size={16} />
              Back to SME Cap Advisory
            </NavLeft>

            <NavLinks>
              <NavLink href="/privacy" $active={path === '/privacy'}>
                Privacy
              </NavLink>
              <NavLink href="/terms" $active={path === '/terms'}>
                Terms
              </NavLink>
            </NavLinks>
          </NavInner>
        </Nav>

        {/* --- HERO --- */}
        <Hero>
          <HeroInner>
            <Eyebrow>SME Cap Advisory</Eyebrow>
            <HeroTitle>Privacy policy</HeroTitle>
            <HeroSub>
              How we collect, use, protect, and govern information when you use
              SME Cap Advisory and smecapadvisory.com.
            </HeroSub>
          </HeroInner>
        </Hero>

        {/* --- CONTENT --- */}
        <Section>
          <Inner>
            <Card>
              <Paragraph>
                This privacy notice discloses the privacy practices for
                smecapadvisory.com.
              </Paragraph>

              <Paragraph>
                SME Cap Advisory (“we”, “us”, or “our”) operates the
                smecapadvisory.com website.
              </Paragraph>

              <Paragraph>
                This page informs you of our policies regarding the collection,
                use, and disclosure of personal data when you use our services
                and the choices you have associated with that data.
              </Paragraph>

              <Paragraph>
                By using this website, you agree to the collection and use of
                information in accordance with this policy.
              </Paragraph>

              <Paragraph>
                This privacy notice applies solely to information collected by
                this website. It will notify you of the following:
              </Paragraph>

              <List>
                <li>
                  What personally identifiable information is collected, how it
                  is used, and with whom it may be shared.
                </li>
                <li>
                  What choices are available to you regarding the use of your
                  data.
                </li>
                <li>
                  The security procedures in place to protect your information.
                </li>
                <li>
                  How you can correct any inaccuracies in the information.
                </li>
              </List>

              <Divider />

              <SectionTitle>
                Information collection, use, and sharing
              </SectionTitle>

              <Paragraph>
                We are the sole owners of the information collected on this site.
                We only collect information that you voluntarily provide when
                using the site or engaging with us.
              </Paragraph>

              <Paragraph>
                We use your information to respond to enquiries and deliver our
                advisory services. We do not sell or rent your information to
                third parties.
              </Paragraph>

              <Paragraph>
                Unless you request otherwise, we may contact you regarding
                updates to our services or changes to this policy.
              </Paragraph>

              <Paragraph>
                We may collect anonymous statistical information on website
                usage through analytics tools. No personally identifiable
                information is shared.
              </Paragraph>

              <Divider />

              <SectionTitle>
                Your access to and control over information
              </SectionTitle>

              <Paragraph>
                You may request access to, correction of, or deletion of your
                personal information at any time, subject to legal or regulatory
                obligations.
              </Paragraph>

              <Paragraph>
                Requests can be sent to{' '}
                <EmailLink href="mailto:team@smecapadvisory.com">
                  team@smecapadvisory.com
                </EmailLink>
                .
              </Paragraph>

              <Divider />

              <SectionTitle>Security</SectionTitle>

              <Paragraph>
                We take reasonable steps to protect your information through
                appropriate technical and organisational safeguards.
              </Paragraph>

              <Paragraph>
                Where sensitive information is provided, it is transmitted and
                stored securely.
              </Paragraph>

              <Paragraph>
                If you believe we are not complying with this privacy policy,
                please contact us immediately at{' '}
                <EmailLink href="mailto:team@smecapadvisory.com">
                  team@smecapadvisory.com
                </EmailLink>
                .
              </Paragraph>
            </Card>
          </Inner>
        </Section>
      </Page>
    </ThemeProvider>
  );
};

export default PrivacyPage;

/* ======================================================
   STYLES (Light theme)
====================================================== */

const Page = styled.div``;

const Hero = styled.section`
  padding: 160px 24px 0;
`;

const HeroInner = styled.div`
  max-width: 980px;
  margin: 0 auto;
`;

const Eyebrow = styled.div`
  font-size: 13px;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.subText};
  margin-bottom: 14px;
`;

const HeroTitle = styled.h1`
  font-size: clamp(42px, 6vw, 64px);
  font-weight: 500;
  margin: 0 0 12px;
`;

const HeroSub = styled.p`
  font-size: 18px;
  color: ${({ theme }) => theme.subText};
  line-height: 1.7;
`;

const Section = styled.section`
  padding: 80px 24px 140px;
`;

const Inner = styled.div`
  max-width: 980px;
  margin: 0 auto;
`;

const Card = styled.div`
  background: ${({ theme }) => theme.card};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 24px;
  padding: 36px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  margin: 32px 0 14px;
`;

const Paragraph = styled.p`
  font-size: 15px;
  line-height: 1.75;
  color: ${({ theme }) => theme.subText};
  margin: 0 0 14px;
`;

const List = styled.ul`
  padding-left: 20px;
  margin: 12px 0 24px;
  color: ${({ theme }) => theme.subText};

  li {
    margin-bottom: 10px;
    line-height: 1.6;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.border};
  margin: 36px 0;
`;

const EmailLink = styled.a`
  color: ${({ theme }) => theme.text};
  text-decoration: none;
  border-bottom: 1px solid rgba(0,0,0,0.25);

  &:hover {
    border-color: #000;
  }
`;

/* --- Nav --- */

const Nav = styled.div`
  position: fixed;
  top: 16px;
  left: 0;
  right: 0;
  z-index: 90;
  padding: 0 18px;
`;

const NavInner = styled.div`
  max-width: 980px;
  margin: 0 auto;
  background: rgba(255,255,255,0.85);
  backdrop-filter: blur(14px);
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 999px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const NavLeft = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.subText};
  font-size: 14px;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: rgba(0,0,0,0.06);
    color: #000;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 8px;
`;

const NavLink = styled.a`
  font-size: 14px;
  padding: 8px 14px;
  border-radius: 999px;
  text-decoration: none;
  cursor: pointer;
  color: ${({ theme, $active }) =>
    $active ? '#fff' : theme.subText};
  background: ${({ $active }) =>
    $active ? 'rgba(0,0,0,1)' : 'transparent'};
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.border : 'transparent'};
transition: all 0.4s;

${({ $active }) => !$active && css`
    &:hover {
        background: rgba(0,0,0,0.08);
        color: #000;
    }
`}
 
`;
